import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { ConfirmationDialog } from '../../ConfirmationDialog';
import { Button, FormControl, InputLabel, responsiveFontSizes, TextField } from '@material-ui/core';
import { addDays, format, subMonths } from 'date-fns';
import ReactInputMask from 'react-input-mask';
import { useMemo } from 'react';
import { resetInVisionOrder } from '../../../services';
import { useOrderDetailContext } from '../OrderDetailContext';
import Loading from '../../Loading';
import { AppContext } from '../../../AppContext';

const styles = theme => ({
  formControl: {
    marginTop: 10,

    '&:first-of-type': {
      marginTop: 0,
    },
  },

  input: {
    'label + &': {
      marginTop: theme.spacing(2),
    },
    '& .MuiInputBase-input': {
      borderRadius: 4,
      position: 'relative',
      fontSize: 16,
      padding: '10px 12px',
      transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    },
  },
});

const FIELDS = {
  INSURED_PHONE: 'insuredPhone1',
  INSURED_EMAIL: 'insuredEmail',
  HOMEOWNER_DUE_DATE: 'homeownerDueDate',
};

const OrderResetButton = withStyles(styles)(({ className, classes, orderLineId, fetchOrderData }) => {
  // #region STATE
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formIsSaving, setFormIsSaving] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formValues, setFormValues] = useState({
    [FIELDS.HOMEOWNER_DUE_DATE]: format(addDays(new Date(), 7), 'yyyy-MM-dd'),
    [FIELDS.INSURED_PHONE]: '',
  });
  // #endregion

  // #region HOOKS
  const { dispatch } = useContext(AppContext);
  const { state } = useOrderDetailContext();
  // #endregion

  // #region FUNCTIONS
  const handleInputUpdate = field => evt => {
    setFormValues({ ...formValues, [field]: evt.target.value });
  };

  const saveForm = async () => {
    try {
      setFormIsSaving(true);
      setFormError(null);

     await resetInVisionOrder(orderLineId, formValues);

      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          message: 'Order Updated!',
          variant: 'success',
        },
      });

      setDialogOpen(false);
      //fetchOrderData();
    } catch (error) {
      console.log(error);
      setFormError(error.Message);
      dispatch({
        type: 'SET_SNACKBAR',
        snackbar: {
          isOpen: true,
          variant: 'error',
          message: error.Message || 'There was an error relaunching this order',
        },
      });
    } finally {
      setFormIsSaving(false);
    }
  };
  // #endregion

  // #region COMPUTED
  const formIsValid = useMemo(() => {
    const phoneValid = formValues[FIELDS.INSURED_PHONE]?.replace(/-|_/g, '').length === 10; // filter out dashes and underscrores from mask
    const dueDateValid = !!formValues[FIELDS.HOMEOWNER_DUE_DATE];

    return [
      //
      phoneValid,
      dueDateValid,
    ].every(Boolean);
  }, [formValues]);
  const importLine = state?.detail?.importLine;
  // #endregion

  // #region EFFECTS
  useEffect(() => {
    if (importLine) {
      const phoneNumber = importLine?.insuredphone1?.replace(/\+1/g, '');

      setFormValues({
        ...formValues,
        [FIELDS.INSURED_PHONE]: phoneNumber,
        [FIELDS.INSURED_EMAIL]: importLine?.insuredEmail,
      });
    }
  }, [importLine]);
  // #endregion

  return (
    <>
      <ConfirmationDialog
        visible={dialogOpen}
        setVisibility={() => setDialogOpen(false)}
        title="Relaunch InVisionIQ"
        style={{
          display: 'flex',
          flexDirection: 'column',
        }}
        minHeight={1}
        isSubmitting={formIsSaving}
        submitMessage={
          <div
            style={{
              color: 'var(--jmi-red)',
              textAlign: 'center',
            }}
          >
            {formError}
          </div>
        }
        cancelButton={
          <Button
            variant="contained"
            onClick={() => setDialogOpen(false)}
            style={{
              backgroundColor: 'var(--jmi-red)',
              color: 'white',
            }}
          >
            Cancel
          </Button>
        }
        acceptButton={
          <Button
            variant="contained"
            disabled={!formIsValid || formIsSaving}
            onClick={saveForm}
            style={{
              backgroundColor: formIsValid ? 'var(--jmi-blue)' : undefined,
              color: formIsValid ? 'white' : undefined,
            }}
          >
            <span
              style={{
                opacity: formIsSaving ? 0.35 : 1,
              }}
            >
              Confirm
            </span>

            {formIsSaving && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  bottom: 0,
                  left: 0,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Loading
                  style={{
                    height: 25,
                    width: 25,
                    color: 'white',
                  }}
                />
              </div>
            )}
          </Button>
        }
      >
        <FormControl variant="standard" className={classes.formControl}>
          <InputLabel shrink htmlFor={FIELDS.INSURED_PHONE}>
            Insured Phone Number
          </InputLabel>

          <ReactInputMask
            mask="999-999-9999"
            value={formValues[FIELDS.INSURED_PHONE]}
            onChange={handleInputUpdate(FIELDS.INSURED_PHONE)}
            disabled={false}
            maskChar="_"
          >
            {() => (
              <TextField
                //
                type="text"
                name={FIELDS.INSURED_PHONE}
                variant="outlined"
                className={classes.input}
              />
            )}
          </ReactInputMask>
        </FormControl>

        <FormControl variant="standard" className={classes.formControl}>
          <InputLabel shrink htmlFor={FIELDS.EMAIL}>
            Insured Email
          </InputLabel>
          <TextField
            type="text"
            name={FIELDS.INSURED_EMAIL}
            variant="outlined"
            value={formValues[FIELDS.INSURED_EMAIL]}
            onChange={handleInputUpdate(FIELDS.INSURED_EMAIL)}
            className={classes.input}
          />
        </FormControl>

        <FormControl variant="standard" className={classes.formControl}>
          <InputLabel shrink htmlFor={FIELDS.HOMEOWNER_DUE_DATE}>
            Due Date
          </InputLabel>
          <TextField
            type="date"
            name={FIELDS.HOMEOWNER_DUE_DATE}
            variant="outlined"
            value={formValues[FIELDS.HOMEOWNER_DUE_DATE]}
            onChange={handleInputUpdate(FIELDS.HOMEOWNER_DUE_DATE)}
            className={classes.input}
          />
        </FormControl>
      </ConfirmationDialog>

      <Button
        onClick={() => setDialogOpen(true)}
        variant="outlined"
        className={className}
      >
        Relaunch InvisionIQ
      </Button>
    </>
  );
});

OrderResetButton.propTypes = {
  className: PropTypes.object,
  onSave: PropTypes.func,
};

export default OrderResetButton;
